import Tooltip from '@codegouvfr/react-dsfr/Tooltip';
import { useTextes } from '@common/config';
import { useDeferredAction } from '@common/hooks';
import { EFFECTIF_APPRENTI_PLACEHOLDER, NB_RNCP_PLACEHOLDER, NDA_PLACEHOLDER } from '@common/schemas';
import { UAI_PLACEHOLDER } from '@common/schemas/etablissement';
import {
  EtablissementInfoDto,
  EtablissementRuleInfoDto,
  NdaCodeDto,
  NdaSiretDto,
  OrganismeDetailsDto,
  TypeDeclaration,
  useAddOrganismeEtablissementMutation,
  useGetSiretBySirenAndCodeQuery,
  useUpdateOrganismeEtablissementMutation,
} from '@common/services';
import {
  CheckboxField,
  Form,
  FormField,
  FormMethodsRef,
  SiretFormField,
  SiretFormFieldHandle,
  YesNoRadioGroupField,
} from '@common/ui';
import { areSomePropertiesDifferent } from '@common/utils';
import React, { forwardRef, Ref, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { ZodObject } from 'zod';
import {
  EditEtablissementInfoFormResult,
  editEtablissementInfoFormSchema,
} from '../schemas/edit-etablissement-info-form.schema';
import { useAppDispatch } from '../../../../../apps/parcours-ofa/src/hooks/store.hook';
import { etablissementModified } from '../../../../../apps/parcours-ofa/src/pages/Dashboard/components/VerticalStepper/stores/dashboard-stepper.slice'

export const EditEtablissementInfoForm = forwardRef<EtablissementEditInfoModaldHandles, EditOfaEtablissementFormProps>(
  ({
     etablissement,
     etablissementPrincipal,
     isAddMode,
     organisme,
     buttonSubmitRef,
     formMethodsRef,
     submitHandler,
     typeDeclarationChangeHandler,
   }: EditOfaEtablissementFormProps, ref) => {

    const t = useTextes('Commun', 'Etablissements');

    const dispatch = useAppDispatch();

    const { id: organismeId, siren, nda: organismeNda } = organisme ?? {};

    const siretFormRef = useRef<SiretFormFieldHandle | null>(null!);

    const [formSchema, setFormSchema] = useState<ZodObject>(editEtablissementInfoFormSchema);
    const [isPrincipal, setIsPrincipal] = useState<boolean>(false);
    const [isSiretValid, setIsSiretValid] = useState<boolean>(!isAddMode); // on initialise à l'inverse de `isAddMode` car en mode edition le siret est considéré comme valide et non modificable car il est bloqué
    const [disabledTooltipMessage, setDisabledTooltipMessage] = useState<string>('');

    const [ruleInfo, setRuleInfo] = useState<EtablissementRuleInfoDto | null>(null);
    const [ndas, setNdas] = useState<NdaCodeDto[]>([]);

    const { prepareAction, performAction } = useDeferredAction();

    const [addOrganismeEtablissement] = useAddOrganismeEtablissementMutation();
    const [updateOrganismeEtablissement] = useUpdateOrganismeEtablissementMutation();

    const { data: ndaDto = {} as NdaSiretDto, error: ndaError } = useGetSiretBySirenAndCodeQuery(
      { siren, code: organismeNda },
      { skip: !organismeNda || !!etablissementPrincipal },
    );

    const isFirstEtablissementToAdd = (isAddMode && !etablissementPrincipal);
    const isSiretFoundForFirstEtablissementToAdd = isFirstEtablissementToAdd && !ndaError;

    // Exposer les méthodes que le parent peut appeler
    useImperativeHandle(ref, () => ({
      onTypeDeclarationChangeConfirmation,
    }));

    useEffect(() => {
      // L'établissement est considéré comme principal si :
      // - il est le tout premier à être ajouté
      // - il est déja principal
      const isPrincipalValue = isFirstEtablissementToAdd || !!etablissement?.principal;
      setIsPrincipal(isPrincipalValue);
    }, [isFirstEtablissementToAdd, etablissement]);

    // Si la valeur de isAddMode change, il faut aussi changer isSiretValid.
    // Cela se produit quand on passe de la popin d'ajout puis de la popin de modif.
    useEffect(()=> {
      setIsSiretValid(!isAddMode)
    }, [isAddMode])

    useEffect(() => {
      const isPrincipalValue = isFirstEtablissementToAdd || !!etablissement?.principal;

      if (etablissement?.principal) {
        setDisabledTooltipMessage('La désignation principale ne peut pas être retirée de l\'établissement directement. Veuillez désigner un autre établissement principal pour cela.');
      } else if (etablissement?.typeDeclaration === TypeDeclaration.INDIVIDUELLE) {
        setDisabledTooltipMessage('Un établissement faisant une déclaration individuelle ne peut pas devenir principal déclarant. Pour cela il doit faire partie de la déclaration consolidée.');
      }

      // On modifie le schema du formulaire en fonction des règles décrites ci-dessous
      setFormSchema(editEtablissementInfoFormSchema.safeExtend({
        principal: getLockedFieldOnCondition('principal', (isPrincipalValue || etablissement?.typeDeclaration === TypeDeclaration.INDIVIDUELLE)), // On verrouille la coche "principal" si l'établissement est déjà principal (car un autre doit être nommé à sa place pour le remplacer) ou si la déclaration est individuelle, seul un établissement faisant partie de la consolidation peut être principal
        siret: getLockedFieldOnCondition('siret', (!isAddMode || isSiretFoundForFirstEtablissementToAdd)),  // Le siret est verrouillé en mode "édition" ou si un SIRET est trouvé avec le NDA de l'organisme pour le 1er établissement
        declarationIndividuelle: getLockedFieldOnCondition('declarationIndividuelle', isPrincipalValue || isPrincipal), // Si c'est l'établissement principal, on ne peut pas modifier le type de la déclaration
        nda: getLockedFieldOnCondition('nda', isPrincipalValue || isPrincipal), // Le nda est verrouillé lorsque si l'établissement est principal ou le 1er à être enregistré
      }));
    }, [etablissement?.principal, isAddMode, isPrincipal, isFirstEtablissementToAdd, isSiretFoundForFirstEtablissementToAdd]);

    // On reset les infos de RULE et les NDAs lorsqu'on bascule en mode "ajout"
    useEffect(() => {
      setRuleInfo(null);
      setNdas([]);
    }, [isAddMode]);

    const values: Partial<EditEtablissementInfoFormResult> = useMemo(() => {
      // Si c'est l'ajout du tout 1er établissement, il est forcément principal, son SIRET de la base DGEFP si trouvé avec ses infos provenant de RULE
      if (isFirstEtablissementToAdd) return {
        principal: true,
        nda: organismeNda,
        siret: ruleInfo?.siret || ndaDto.siret,
        denominationUsuelle: ruleInfo?.denominationUsuelle ?? '',
        adresse: ruleInfo?.adresse ?? '',
        complementAdresse: ruleInfo?.complementAdresse ?? '',
        codePostal: ruleInfo?.codePostal ?? '',
        ville: ruleInfo?.ville ?? '',
        declarationIndividuelle: 'false',
      };

      // Si c'est l'édition d'un établissement existant, on s'appuie sur les données de `etablissement` issues de RTK query
      if (!isAddMode && etablissement) return {
        ...etablissement,
        // Impossible de changer dynamiquement le declarationIndividuelle sans le reset.
        // TODO lorsqu'il y a des changements dynamiques sur le  formulaire, migrer "values" -> "defaultValues"+"reset" (plus performant et ne casse pas les champs)
        declarationIndividuelle: (etablissement.principal) ? 'false' :
          (etablissement.typeDeclaration === TypeDeclaration.INDIVIDUELLE) ? 'true'
            : 'false',
      };

      // Sinon, c'est nécessairement un ajout d'un établissement secondaire
      return {
        principal: false,
        nda: (ndas.length === 0) ? '' // Liste vide, on ne met rien
          : (ndas.length === 1) ? ndas[0].code // Un seul résultat, on le met
            : organismeNda, // Plusieurs NDAs trouvés, on met celui de l'OFA
        siret: ruleInfo?.siret ?? '',
        denominationUsuelle: (ruleInfo)
          ? (ruleInfo?.denominationUsuelle || organisme.denominationUsuelle || organisme.raisonSociale) // Fallbacks pour ne pas avoir de dénomination usuelle vide
          : '',
        adresse: ruleInfo?.adresse ?? '',
        complementAdresse: ruleInfo?.complementAdresse ?? '',
        codePostal: ruleInfo?.codePostal ?? '',
        ville: ruleInfo?.ville ?? '',
        declarationIndividuelle: 'false',
      };
    }, [isFirstEtablissementToAdd, ndaDto, isAddMode, etablissement, organisme, ruleInfo]);

    const onTypeDeclarationChangeConfirmation = async () => {
      await performAction();
      dispatch(etablissementModified())
      submitHandler();
    };

    const handleSubmit = async (result: EditEtablissementInfoFormResult) => {
      if (!isSiretValid) {
        // si le siret n'est toujours pas valide, on force la dernière erreur pour l'afficher
        forceErrorSiret();
        return;
      }

      const { id, declarationIndividuelle, ...body } = result;

      const typeDeclaration = body.principal ? TypeDeclaration.CONSOLIDEE
        : (declarationIndividuelle === 'true') ? TypeDeclaration.INDIVIDUELLE
          : TypeDeclaration.CONSOLIDEE;

      // Sans ID, c'est une création d'établissement
      if (!id) {
        await addOrganismeEtablissement({
          id: organismeId,
          etablissementBody: { ...body, typeDeclaration },
        }).unwrap();

        setRuleInfo(null);
        setNdas([]);
        setIsSiretValid(false);

        dispatch(etablissementModified());
        submitHandler();
        return;
      }

      // cas impossible
      if (!etablissement) return;

      prepareAction(() => updateOrganismeEtablissement({
        id: organismeId,
        etablissementId: etablissement.id,
        etablissementBody: { ...body, typeDeclaration, nda: (body.principal) ? organismeNda : body.nda , principal: etablissement.principal ? true : body.principal},
      }).unwrap());

      // si aucune donnée mise a jours, on ferme la dialog en faisant appelle au submitHandler
      if (!isEtablissementNeedUpdate(etablissement, result) &&
        etablissement.typeDeclaration == typeDeclaration) {
        submitHandler();
        return;
      }

      // Si le type de déclaration change, on affiche une modale de confirmation
      if (etablissement.typeDeclaration !== typeDeclaration) {
        typeDeclarationChangeHandler(typeDeclaration);
        return;
      }

      // Sinon, on met à jour directement
      await performAction();
      dispatch(etablissementModified());
      submitHandler();
    };

    const handleFormError = () => {
      // si le siret n'est toujours pas valide, on force la dernière erreur pour l'afficher lorsque le formulaire est en erreur
      if (!isSiretValid) forceErrorSiret();
    };

    const forceErrorSiret = () => {
      // Le parent appelle la méthode de l'enfant avec un string
      siretFormRef.current?.forceError();
    };

    return (
      <>
        <Form<EditEtablissementInfoFormResult>
          onSubmit={handleSubmit}
          onFormError={handleFormError}
          values={values}
          className={'flex flex-col gap-6 '}
          schema={formSchema}
          buttonSubmitRef={buttonSubmitRef}
          methodsRef={formMethodsRef}>

          <div className={'flex flex-col gap-4'}>
            <label className={`flex items-center gap-4`}>
              <Tooltip title={disabledTooltipMessage}
                       kind="hover"
                       style={{ display: (etablissement?.principal || etablissement?.typeDeclaration === TypeDeclaration.INDIVIDUELLE) ? 'unset' : 'none' }}>
                <CheckboxField name="principal" onChange={setIsPrincipal} />
              </Tooltip>
              <span>Désigner cet établissement en tant qu'établissement principal ?</span>
            </label>
            {(!etablissement?.principal && !!etablissementPrincipal && isPrincipal) && (
              <span className={'text-orange-500'}>À la validation, cet établissement deviendra principal à la place de l'établissement {etablissementPrincipal?.denominationUsuelle} et récupérera ainsi le NDA {organismeNda} renseigné sur l'OFA</span>
            )}
          </div>

          <SiretFormField name="siret"
                          ref={siretFormRef}
                          organismeId={organismeId}
                          siren={siren}
                          onSiretValidChange={setIsSiretValid}
                          onRuleInfoChange={setRuleInfo}
                          onNdasChange={setNdas}
                          isAddMode={isAddMode} />

          <FormField name="denominationUsuelle" title="Dénomination usuelle" />
          <FormField name="nda" title="NDA" placeholder={NDA_PLACEHOLDER}
                     tooltip={t['DONNEE_OFA_ETAB_NDA_INFO_BULLE_MSG']} />
          <FormField name="uai" title="UAI" placeholder={UAI_PLACEHOLDER} />
          <FormField name="effectifApprenti" title="Effectif d'apprentis" placeholder={EFFECTIF_APPRENTI_PLACEHOLDER} />
          <FormField name="adresse" title="Adresse" />
          <FormField name="complementAdresse" title="Complément d'adresse" />
          <FormField name="codePostal" title="Code postal" />
          <FormField name="ville" title="Ville" />
          <FormField name="nbRncp"
                     title="Nombre de certifications (fiches RNCP) que cet établissement effectue en apprentissage"
                     placeholder={NB_RNCP_PLACEHOLDER} />

          <YesNoRadioGroupField name="declarationIndividuelle"
                                title="Souhaitez-vous établir une déclaration individuelle pour cet établissement ?" />
        </Form>
      </>
    );
  });

function getLockedFieldOnCondition(key: keyof EditEtablissementInfoFormResult, condition: boolean) {
  const subSchema: any = editEtablissementInfoFormSchema.shape[key];
  return (condition) ? subSchema.readonly() : subSchema;
}

function isEtablissementNeedUpdate(etablissement: EtablissementInfoDto, result: EditEtablissementInfoFormResult) {
  return areSomePropertiesDifferent(
    etablissement, result,
    ['principal', 'nda', 'uai', 'adresse',
      'complementAdresse', 'codePostal', 'ville', 'effectifApprenti',
      'denominationUsuelle', 'siret', 'nbRncp'],
  );
}

export interface EtablissementEditInfoModaldHandles {
  onTypeDeclarationChangeConfirmation: () => Promise<void>;
}

export interface EditOfaEtablissementFormProps {
  etablissement?: EtablissementInfoDto;
  etablissementPrincipal?: EtablissementInfoDto;
  isAddMode: boolean;
  organisme: OrganismeDetailsDto;
  buttonSubmitRef: Ref<HTMLButtonElement>;
  formMethodsRef?: Ref<FormMethodsRef>;
  submitHandler: () => void,
  typeDeclarationChangeHandler: (TypeDeclaration) => void;
}